import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId } from '../utils/orgUtils'

function withOrganizationScope(url: string): string {
  const orgId = getActiveOrgId()
  if (!orgId) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}organization_id=${encodeURIComponent(orgId)}`
}

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
}

async function parseJson(res: Response) {
  const body = await res.json()
  if (!res.ok) throw new Error(body?.message || body?.error || 'Request failed')
  return body?.data ?? body
}

export type OperationalControlStateRow = {
  id: string
  controlHealth: string
  assuranceLevel: string
  telemetryConfidence: number
  operationalPressure: number
  lastVerifiedAt?: string
  nextVerificationAt?: string | null
  explanations?: { summary?: string; lines?: Array<{ fallback: string }> }
  canonicalControl?: { id: string; code: string }
  informationSystem?: { id: string; name: string } | null
  standardCode?: string | null
}

export async function fetchControlHealth(standardCode?: string) {
  const q = standardCode ? `?standardCode=${encodeURIComponent(standardCode)}` : ''
  const res = await fetch(withOrganizationScope(`${API_ENDPOINTS.CONTROL_ASSURANCE_HEALTH}${q}`), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as {
    states: OperationalControlStateRow[]
    summary: Record<string, number>
    graphOverlay?: { nodes: unknown[]; edges: unknown[] }
  }
}

export async function fetchDegradedControls(standardCode?: string) {
  const q = standardCode ? `?standardCode=${encodeURIComponent(standardCode)}` : ''
  const res = await fetch(withOrganizationScope(`${API_ENDPOINTS.CONTROL_ASSURANCE_DEGRADED}${q}`), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as { degraded: OperationalControlStateRow[]; count: number }
}

export async function fetchOperationalDrift(standardCode?: string) {
  const q = standardCode ? `?standardCode=${encodeURIComponent(standardCode)}` : ''
  const res = await fetch(withOrganizationScope(`${API_ENDPOINTS.CONTROL_ASSURANCE_DRIFT}${q}`), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as { drift: Array<{ id: string; driftType: string; severity: string; description: string }> }
}

export async function fetchOperationalPressure(standardCode?: string) {
  const q = standardCode ? `?standardCode=${encodeURIComponent(standardCode)}` : ''
  const res = await fetch(withOrganizationScope(`${API_ENDPOINTS.CONTROL_ASSURANCE_PRESSURE}${q}`), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as {
    controls: Array<{ id: string; controlCode?: string; operationalPressure: number; controlHealth: string }>
  }
}

export async function fetchAutomatedEvidence() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.CONTROL_ASSURANCE_AUTOMATED_EVIDENCE), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as {
    automatedEvidence: Array<{ id: string; title: string; evidenceSource?: string; verificationStatus?: string }>
  }
}

export async function recomputeControlAssurance(standardCode?: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.CONTROL_ASSURANCE_RECOMPUTE), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ standardCode }),
  })
  return parseJson(res)
}

export async function verifyOperationalControl(controlId: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.CONTROL_ASSURANCE_VERIFY(controlId)), {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}
